// @flow

import _ from 'lodash';

import z from 'app/common/z';
import { APP_STATUSES } from 'app/developer/constants';

export const isDeployInProgress = (statusUpdate: Object) => {
  if (_.isEmpty(statusUpdate)) {
    return false;
  } else if (
    statusUpdate.status === 'scheduled' ||
    statusUpdate.status === 'in_progress'
  ) {
    return true;
  }

  return false;
};

export const wasDeployAttempted = (statusUpdate: Object) => {
  if (_.isEmpty(statusUpdate)) {
    return false;
  } else if (
    statusUpdate.message ||
    statusUpdate.status === 'completed' ||
    statusUpdate.status === 'failed'
  ) {
    return true;
  }

  return false;
};

export type Field = {
  key: string,
  imageUrl?: string,
  value?: string,
  helpText?: string,
  helpTextHtml?: string,
  help_text?: string,
  help_text_html?: string,
  choices?: Array<{ label: string, value: string }>,
  checkboxLabel?: string,
};

export const mapFormFields = (
  fields: Array<Field>,
  values: Object
): Array<Field> => {
  const mappedFields = fields.map((field: Field): Field => {
    const clonedField = _.clone(field);

    // Special case for "image" field, where we need an imageFile and imageUrl
    // Mostly for preview and backbone-sync-not-going-crazy purposes
    if (field.key === 'image') {
      clonedField.key = 'imageFile';
      clonedField.imageUrl = _.get(values, 'image');
    }

    // Special case for "role" field, where the choices depends on the title.
    if (field.key === 'role' && !_.isEmpty(field.choices)) {
      const appName = values.title;
      clonedField.choices = _.map(field.choices, ({ label, ...rest }) => ({
        label: _.replace(label, '{title}', appName || '____'),
        ...rest,
      }));
    }

    // Add .value based on values[*]
    clonedField.value = _.get(values, clonedField.key);

    // Add helpTextHtml based on help_text_html
    clonedField.helpTextHtml = field.help_text_html;

    // Special case for "app_category" field, omit "other" category.
    if (field.key === 'app_category' && !_.isEmpty(field.choices)) {
      clonedField.choices = _.filter(
        field.choices,
        choice => choice.value !== 'other'
      );
    }

    // Special case for "subscription" field, where helpText is really the checkbox label text.
    // This fits with `SingleCheckboxField` where we can have:
    // - field label (field.label) Displays above the checkbox like other formatic inputs
    // - field helpText (field.helpText -- not used here but possible given a server change)
    // - checkbox label (field.checkboxLabel) Displays next to the checkbox
    if (field.key === 'subscription') {
      // Add helpText based on help_text
      clonedField.checkboxLabel = field.help_text;
      clonedField.helpTextHtml = '';
    }

    // Remove .help_text and .help_text_html
    delete clonedField.help_text;
    delete clonedField.help_text_html;

    return clonedField;
  });

  return mappedFields;
};

export const getPrettyVersion = (version: string) => {
  if (version === '2015-07-31T00:00:00') {
    return '2';
  } else if (version === '2012-08-01T00:00:00') {
    return '1';
  } else {
    return 'CLI';
  }
};

// Will format/handle specific form field errors, or just global/generic ones
export const formatFieldErrors = (error: Object) => {
  const resp = JSON.parse(error.response.responseText);

  if (_.isArray(resp.errors)) {
    return {
      errors: resp.errors,
      fieldErrors: {},
    };
  }

  if (_.isObject(resp.errors)) {
    return {
      errors: [],
      fieldErrors: {
        ...resp.errors,
        // Move "image" to "imageFile"
        imageFile: _.cloneDeep(resp.errors.image),
        image: undefined,
      },
    };
  }

  return {
    errors: [],
    fieldErrors: {},
  };
};

export const defaultLogo = `${z.cfg.mediaURL}images/frontend/placeholder/128x128.png`;

export const newEntityIds = {
  COLLABORATOR: 'new-collaborator',
  INVITEE: 'new-invitee',
};

export const getPrettyCliVisibility = (
  isPublic: Boolean,
  isPending: Boolean
) => {
  if (isPublic) {
    return {
      title: 'Public',
      summary: 'public apps are apps that anyone on Zapier can see and use.',
    };
  }

  if (isPending) {
    return {
      title: 'Early Access/Pending',
      summary:
        'early access/pending apps are being monitored by Zapier for quality and usage.',
    };
  }

  return {
    title: 'Private',
    summary:
      'private apps can be used just like any app on Zapier, but only by yourself and those who are invited/admins.',
  };
};

// Map CLI Apps to a V2-like shape
export const cliToV2 = (app: Object) => {
  const status = app.public
    ? APP_STATUSES.GLOBAL
    : app.pending
    ? APP_STATUSES.PENDING
    : APP_STATUSES.PRIVATE;

  return {
    id: app.id,
    title: app.title,
    status,
    isEditable: true,
    image: app.image || defaultLogo,
    isCli: true,
    canDelete: !app.public,
  };
};

// For AppStats component, related to AppStatsShape
export const getListStatsFromPlain = (plainStats: Object) => {
  const listStats = [];

  _.each(plainStats, (items, type) => {
    _.each(items, (values, label) => {
      const item = {
        Type: type,
        Label: label,
      };
      listStats.push(_.extend(item, values));
    });
  });

  return listStats;
};

// For style checks
export const parseStyleCheckResult = (data: Object) => {
  const resourceMap = {
    read: 'Trigger',
    write: 'Action',
    search: 'Search',
    auth: 'Auth',
    meta: 'Meta',
  };
  const issues = {
    errors: [],
    warnings: [],
  };
  const docsDomainUrl = '/developer/documentation/v2/style-checks-reference';

  for (const issueType in data) {
    for (const resourceType in data[issueType]) {
      for (const resourceName in data[issueType][resourceType]) {
        const issueDetails = ['<ol>'];
        data[issueType][resourceType][resourceName].forEach(value => {
          // The specific issue tag is at the end of the value, enclosed in
          // parens. It is always six characters long, so if we take the last 7,
          // you get the tag plus closing paren, which we strip off.
          // example: 'cooking MCs like a pound of bacon (ZIC321)'
          const docsTag = value.substr(-7).replace(')', '');
          const docsLink = `(<a href="${docsDomainUrl}#${docsTag}" target="_blank">${docsTag}</a>)`;
          issueDetails.push(
            '<li>' + _.escape(value).replace(`(${docsTag})`, docsLink) + '</li>'
          );
        });

        issueDetails.push('</ol>');

        if (issues.hasOwnProperty(issueType)) {
          issues[issueType].push(
            `${
              resourceMap[resourceType]
            } (${resourceName}): ${issueDetails.join('')}`
          );
        }
      }
    }
  }

  return issues;
};

const DeveloperUtils = {
  isDeployInProgress,
  wasDeployAttempted,
  defaultLogo,
  getPrettyVersion,
  formatFieldErrors,
  mapFormFields,
  newEntityIds,
  getPrettyCliVisibility,
  cliToV2,
  getListStatsFromPlain,
  parseStyleCheckResult,
};

export default DeveloperUtils;
